import React, { useRef, useState } from 'react';
import { FaCloudUploadAlt, FaImage, FaTimes } from 'react-icons/fa';
import { api } from '../api';

const ACCEPTED_TYPES = 'image/jpeg,image/jpg,image/png,image/svg+xml,image/webp';

const ImageUploadField = ({ label, value, onChange, onUploadMeta, required = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await api.uploadImage(file);
      onChange(uploaded.fileUrl);
      if (onUploadMeta) {
        onUploadMeta({
          fileName: uploaded.fileName,
          fileUrl: uploaded.fileUrl,
          contentType: uploaded.contentType,
          size: uploaded.size,
        });
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const clearImage = () => {
    onChange('');
    setUploadError('');
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        {label}
        {required ? ' *' : ''}
      </label>

      {value ? (
        <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-[#0d1525]">
          <img src={value} alt={label} className="h-36 w-full object-cover" />
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2 dark:border-white/10">
            <p className="truncate text-xs text-slate-500 dark:text-gray-400">{value}</p>
            <button
              type="button"
              onClick={clearImage}
              className="rounded-md border border-red-500/40 px-2 py-1 text-xs font-semibold text-red-500 hover:bg-red-500/10 dark:text-red-400"
            >
              <FaTimes size={10} className="inline mr-1" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-2 flex h-28 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-white/20 dark:bg-[#0d1525] dark:text-gray-500">
          <div className="text-center">
            <FaImage className="mx-auto mb-1 text-lg" />
            <p className="text-xs">No image selected</p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={handleFileSelect}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5 text-sm font-bold text-primary hover:bg-primary/20 disabled:opacity-60"
      >
        <FaCloudUploadAlt size={14} />
        {uploading ? 'Uploading...' : value ? 'Replace Image' : 'Upload Image'}
      </button>

      <p className="mt-1 text-[11px] text-slate-400 dark:text-gray-500">JPG, PNG, SVG, WEBP (max recommended 5MB)</p>
      {uploadError ? <p className="mt-1 text-xs text-red-500 dark:text-red-400">{uploadError}</p> : null}
    </div>
  );
};

export default ImageUploadField;
